import type { IAIService } from "@/services/interfaces/IAIService";
import type {
  AIDecompositionResult,
  DecomposeContext,
  EffortEstimate,
  RankedBounty,
  SampleTask,
  RolePreference,
} from "@/lib/types";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { InferredSkills } from "@/lib/skills";

export class SupabaseAIService implements IAIService {

  async decomposeQuest(
    title: string,
    description: string,
    context?: DecomposeContext,
  ): Promise<AIDecompositionResult> {
    return callEdgeFunction<AIDecompositionResult>("ai-decompose", {
      body: { title, description, context },
    });
  }

  async suggestSkills(taskDescription: string): Promise<string[]> {
    // Local heuristic for now — no Edge Function needed
    const keywords = taskDescription.toLowerCase();
    const suggestions: string[] = [];
    if (keywords.includes("react") || keywords.includes("frontend"))
      suggestions.push("React", "TypeScript", "Tailwind CSS");
    if (keywords.includes("design") || keywords.includes("ui"))
      suggestions.push("UI Design", "Figma");
    if (keywords.includes("write") || keywords.includes("content"))
      suggestions.push("Writing", "Editing", "SEO");
    if (keywords.includes("data") || keywords.includes("analy"))
      suggestions.push("Data Analysis", "Python", "SQL");
    return suggestions.length > 0
      ? suggestions
      : ["Research", "Writing", "Communication"];
  }

  async estimateEffort(taskDescription: string): Promise<EffortEstimate> {
    const words = taskDescription.split(/\s+/).length;
    const hours = Math.max(1, Math.round(words / 10));
    return { hours: Math.min(hours, 40), confidence: 0.7 };
  }

  async matchBountiesToUser(
    _userId: string,
    availableBounties: string[],
  ): Promise<RankedBounty[]> {
    return availableBounties.map((id, i) => ({
      bountyId: id,
      matchScore: Math.max(0.3, 1 - i * 0.1),
    }));
  }

  async generateSampleTask(
    skills: string[],
    _role: RolePreference,
  ): Promise<SampleTask> {
    // TODO: Replace with Edge Function call when available
    // For now, return a skill-matched fallback
    return {
      title: "Research and build a competitive analysis report",
      description: `Analyze competitors based on your skills in ${skills.slice(0, 3).join(", ")}. AI gathers data, you provide strategic insights.`,
      budget: 160,
      aiHumanSplit: "AI handles 40%, you handle 60%",
      estimatedTime: "~3 hours",
    };
  }

  async inferSkills(text: string): Promise<InferredSkills> {
    // `other` is absent from deployments that predate it.
    const { skills, other } = await callEdgeFunction<{
      skills?: string[];
      other?: string[];
    }>("ai-infer-skills", { body: { text } });
    return { skills: skills ?? [], other: other ?? [] };
  }
}
